/**
 * Core Web Vitals Tracking
 *
 * Metrics & Optimization System § 4.3 Render Performance Metrics
 *
 * Tracks Google Core Web Vitals metrics for performance monitoring:
 * - LCP (Largest Contentful Paint): Time to render largest element
 * - FCP (First Contentful Paint): Time to first rendered content
 * - TTI (Time to Interactive): Time until page is interactive
 * - CLS (Cumulative Layout Shift): Visual stability score
 * - FID (First Input Delay): Time to first user interaction
 *
 * These metrics are sent to the backend performance_metrics table
 * for real-time dashboards and alerting.
 *
 * Usage:
 * ```
 * import { initWebVitals } from '@/utils/webVitals';
 *
 * // Call in app initialization (e.g., main.tsx)
 * initWebVitals();
 * ```
 */

/**
 * Metric data from web-vitals library
 */
interface Metric {
  name: string;
  value: number;
  rating?: 'good' | 'needs-improvement' | 'poor';
  delta?: number;
  id: string;
}

/**
 * Performance metric thresholds (from Web Vitals guidance)
 */
const METRIC_THRESHOLDS = {
  LCP: 2500, // Largest Contentful Paint (good: <2.5s)
  FCP: 1800, // First Contentful Paint (good: <1.8s)
  TTI: 3800, // Time to Interactive (good: <3.8s)
  CLS: 0.1, // Cumulative Layout Shift (good: <0.1)
  FID: 100, // First Input Delay (good: <100ms, deprecated — superseded by INP)
  INP: 200, // Interaction to Next Paint (good: <=200ms) — web-vitals v4+ FID successor
};

/**
 * Send metric to backend performance_metrics table
 */
async function sendToBackend(metricType: string, value: number): Promise<void> {
  try {
    // Don't send if offline
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      return;
    }

    // Non-PII payload: only a metric label and an integer duration (I-AX-03).
    const params = new URLSearchParams({
      metric_type: metricType,
      duration_ms: Math.round(value).toString(),
    });
    const url = `/api/v1/metrics/performance?${params.toString()}`;

    // Prefer sendBeacon: LCP/INP/CLS are reported at page-hide, where a plain
    // fetch is often cancelled by the unloading document. sendBeacon survives it.
    // The query string carries the payload; the body is empty (endpoint reads
    // Query params). Fall back to fetch when sendBeacon is unavailable.
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const queued = navigator.sendBeacon(url);
      if (queued) {
        return;
      }
      // sendBeacon can refuse (e.g. queue full) — fall through to fetch.
    }

    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
    }).catch(() => {
      // Silently fail - don't interfere with page performance
    });
  } catch (e) {
    // Silently fail
  }
}

/**
 * Log metric to console (development only)
 */
function logMetric(metric: Metric): void {
  if (process.env.NODE_ENV === 'development') {
    const rating = metric.rating || 'unknown';
    const threshold = METRIC_THRESHOLDS[metric.name as keyof typeof METRIC_THRESHOLDS];
    const isGood = metric.value <= threshold;

    console.debug(`Web Vital: ${metric.name} = ${metric.value.toFixed(2)}${metric.name === 'CLS' ? '' : 'ms'} (${rating})`, {
      value: metric.value,
      threshold: threshold,
      isGood: isGood,
      delta: metric.delta,
    });
  }
}

/**
 * Initialize Core Web Vitals tracking
 *
 * This function should be called once during app initialization.
 * It sets up listeners for all 5 Core Web Vitals and sends data to backend.
 */
export async function initWebVitals(): Promise<void> {
  try {
    // Dynamically import web-vitals library
    // If not available, gracefully degrade (don't break the app)
    // web-vitals v5: onTTI was never exported; onFID was removed in v4 in
    // favour of onINP (Interaction to Next Paint).
    const { onLCP, onFCP, onCLS, onINP } = await import('web-vitals');

    // LCP: Largest Contentful Paint
    onLCP((metric: Metric) => {
      logMetric(metric);
      sendToBackend('LCP', metric.value);
    });

    // FCP: First Contentful Paint
    onFCP((metric: Metric) => {
      logMetric(metric);
      sendToBackend('FCP', metric.value);
    });

    // CLS: Cumulative Layout Shift
    onCLS((metric: Metric) => {
      logMetric(metric);
      // CLS is unitless (0-1), convert to basis points for clarity
      sendToBackend('CLS', metric.value * 100);
    });

    // INP: Interaction to Next Paint (web-vitals v4+ successor to FID)
    onINP((metric: Metric) => {
      logMetric(metric);
      sendToBackend('INP', metric.value);
    });

    console.debug('Core Web Vitals tracking initialized');
  } catch (e) {
    // web-vitals library not available, gracefully degrade
    console.debug('Web Vitals library not available, skipping initialization');
  }
}

/**
 * Get metric thresholds (for testing/analysis)
 */
export function getMetricThresholds(): Record<string, number> {
  return { ...METRIC_THRESHOLDS };
}

/**
 * Check if metric is in 'good' range
 */
export function isMetricGood(metricType: string, value: number): boolean {
  const threshold = METRIC_THRESHOLDS[metricType as keyof typeof METRIC_THRESHOLDS];
  if (!threshold) return true; // Unknown metric, assume good

  return value <= threshold;
}

/**
 * Get metric rating
 */
export function getMetricRating(metricType: string, value: number): 'good' | 'needs-improvement' | 'poor' {
  const threshold = METRIC_THRESHOLDS[metricType as keyof typeof METRIC_THRESHOLDS];
  if (!threshold) return 'good'; // Unknown metric, assume good

  // Define "good", "needs-improvement", and "poor" ranges
  const poorThreshold = threshold * 1.5;

  if (value <= threshold) {
    return 'good';
  } else if (value <= poorThreshold) {
    return 'needs-improvement';
  } else {
    return 'poor';
  }
}

export default initWebVitals;
